import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Calendar, Clock, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const categories = [
  { name: "All", slug: "all" },
  { name: "TikTok", slug: "tiktok" },
  { name: "SEO", slug: "seo" },
  { name: "Local Business", slug: "local-business" },
  { name: "Web Performance", slug: "web-performance" },
  { name: "Social Media", slug: "social-media" },
];

export const blogPosts = [
  {
    id: 1,
    slug: "how-to-go-viral",
    title: "How to Go Viral: The Complete Guide for Small Businesses",
    excerpt: "Discover the secrets behind viral content and learn how to create posts that get shared thousands of times. We break down the psychology of shareable content.",
    content: `Going viral isn't just luck—it's a science. In this comprehensive guide, we'll break down exactly what makes content spread like wildfire and how your small business can tap into this powerful marketing phenomenon.

## Understanding Viral Content

Viral content typically shares several key characteristics:
- **Emotional resonance**: Content that makes people feel something strongly
- **Shareability**: Easy to share and looks good when shared
- **Timing**: Posted at the right moment
- **Relatability**: People see themselves in the content

## The Psychology Behind Sharing

People share content for several reasons:
1. To look good to their peers
2. To help others
3. To express their identity
4. To feel connected to a community

## Practical Tips for Small Businesses

Start by understanding your audience deeply. What makes them laugh? What frustrates them? What do they aspire to?

Create content that speaks to these emotions, and you'll be well on your way to viral success.`,
    category: "social-media",
    categoryLabel: "Social Media",
    date: "Dec 20, 2025",
    readTime: "8 min read",
    image: "https://images.unsplash.com/photo-1516251193007-45ef944ab0c6?w=800&q=80",
    featured: true,
  },
  {
    id: 2,
    slug: "tiktok-algorithm-2025",
    title: "How the TikTok Algorithm Actually Works in 2025",
    excerpt: "Understanding the TikTok algorithm is key to growing your account. Learn exactly how TikTok decides which videos to show and how to hack the system.",
    content: `The TikTok algorithm is one of the most sophisticated content recommendation systems ever created. Here's everything you need to know about how it works in 2025.

## The For You Page (FYP)

The FYP is where the magic happens. TikTok's algorithm analyzes hundreds of signals to determine what content to show each user.

## Key Ranking Factors

### Watch Time
The most important metric. If people watch your video all the way through (or multiple times), TikTok will show it to more people.

### Engagement
Likes, comments, shares, and saves all signal quality content.

### Video Information
Captions, sounds, and hashtags help TikTok understand what your content is about.

## How to Beat the Algorithm

1. Hook viewers in the first second
2. Keep videos short and punchy
3. Use trending sounds strategically
4. Post consistently
5. Engage with your audience`,
    category: "tiktok",
    categoryLabel: "TikTok",
    date: "Dec 18, 2025",
    readTime: "10 min read",
    image: "https://images.unsplash.com/photo-1559028012-481c04fa702d?w=800&q=80",
    featured: true,
  },
  {
    id: 3,
    slug: "seo-basics-rank-google",
    title: "SEO Basics: How to Rank Your Website on Google",
    excerpt: "A comprehensive guide to search engine optimization for beginners. Learn the fundamentals that will help your website climb the search rankings.",
    content: `Search Engine Optimization (SEO) is the practice of optimizing your website to rank higher in search engine results. Here's your complete beginner's guide.

## What is SEO?

SEO involves making changes to your website design and content to make it more attractive to search engines.

## On-Page SEO

### Title Tags
Your title tag is one of the most important ranking factors. Include your main keyword and keep it under 60 characters.

### Meta Descriptions
While not a direct ranking factor, good meta descriptions improve click-through rates.

### Header Tags
Use H1, H2, and H3 tags to structure your content logically.

## Off-Page SEO

Building quality backlinks from other websites signals to Google that your content is valuable and trustworthy.

## Technical SEO

- Ensure your site loads quickly
- Make it mobile-friendly
- Use HTTPS
- Create an XML sitemap`,
    category: "seo",
    categoryLabel: "SEO",
    date: "Dec 15, 2025",
    readTime: "12 min read",
    image: "https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=800&q=80",
    featured: false,
  },
  {
    id: 4,
    slug: "google-page-speed-guide",
    title: "Google Page Speed: Why It Matters and How to Improve It",
    excerpt: "Site speed directly impacts your SEO and conversion rates. Learn how to test your page speed and the quick fixes that can make a huge difference.",
    content: `Page speed is a critical factor for both user experience and SEO. Google has confirmed it's a ranking factor, and slow sites lose customers.

## Why Speed Matters

- 53% of mobile users abandon sites that take over 3 seconds to load
- A 1-second delay can reduce conversions by 7%
- Google uses speed as a ranking factor

## How to Test Your Speed

Use these free tools:
- Google PageSpeed Insights
- GTmetrix
- WebPageTest

## Quick Wins for Better Speed

### Optimize Images
Compress images and use modern formats like WebP.

### Enable Caching
Browser caching stores files locally so returning visitors load faster.

### Minimize Code
Remove unnecessary CSS and JavaScript.

### Use a CDN
Content Delivery Networks serve your content from servers closer to your users.`,
    category: "web-performance",
    categoryLabel: "Web Performance",
    date: "Dec 12, 2025",
    readTime: "7 min read",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80",
    featured: false,
  },
  {
    id: 5,
    slug: "local-business-ideas-2025",
    title: "10 Profitable Local Business Ideas for 2025",
    excerpt: "Looking to start a local business? Here are 10 proven business ideas that are thriving in local communities and how to get started with each.",
    content: `Starting a local business in 2025 offers incredible opportunities. Here are 10 ideas that are proving profitable across communities.

## 1. Mobile Car Detailing
Low startup costs, high demand. You go to the customer.

## 2. Pet Services
Dog walking, pet sitting, and grooming are always in demand.

## 3. Home Cleaning Services
Residential cleaning has steady demand year-round.

## 4. Personal Training
Fitness is booming. Offer in-home or outdoor sessions.

## 5. Landscaping
Seasonal but highly profitable with the right clients.

## 6. Food Truck
Lower overhead than a restaurant with location flexibility.

## 7. Tutoring Services
Help students succeed while building a rewarding business.

## 8. Photography
Specialize in events, portraits, or real estate.

## 9. Handyman Services
Every homeowner needs reliable help.

## 10. Social Media Management
Help local businesses build their online presence.`,
    category: "local-business",
    categoryLabel: "Local Business",
    date: "Dec 10, 2025",
    readTime: "9 min read",
    image: "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=800&q=80",
    featured: false,
  },
  {
    id: 6,
    slug: "tiktok-shop-setup-guide",
    title: "TikTok Shop: The Complete Setup Guide",
    excerpt: "Step-by-step guide to setting up your TikTok Shop, connecting products, and making your first sale. Everything you need to know in one place.",
    content: `TikTok Shop is revolutionizing e-commerce. Here's your complete guide to getting started.

## What is TikTok Shop?

TikTok Shop allows businesses to sell products directly through the TikTok app, creating a seamless shopping experience.

## Setting Up Your Shop

### Step 1: Apply for Access
Go to TikTok Shop Seller Center and apply for a seller account.

### Step 2: Verify Your Business
Provide the required documentation to verify your business.

### Step 3: Set Up Your Shop
Add your logo, description, and shop policies.

### Step 4: Add Products
Upload product images, descriptions, and pricing.

## Tips for Success

- Use high-quality product photos
- Write compelling descriptions
- Price competitively
- Respond to customer queries quickly
- Create content featuring your products`,
    category: "tiktok",
    categoryLabel: "TikTok",
    date: "Dec 8, 2025",
    readTime: "15 min read",
    image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&q=80",
    featured: false,
  },
  {
    id: 7,
    slug: "local-seo-dominate-area",
    title: "Local SEO: How to Dominate Your Area on Google",
    excerpt: "Local businesses need local SEO. Learn how to optimize your Google Business Profile and rank for 'near me' searches in your area.",
    content: `Local SEO helps businesses appear in local search results. If you serve customers in a specific area, this guide is essential.

## Google Business Profile

Your GBP is the foundation of local SEO. Claim and optimize it fully.

### Optimization Tips
- Complete every field
- Add high-quality photos regularly
- Respond to all reviews
- Post updates weekly
- Add products/services

## Local Keywords

Target keywords with local intent:
- "plumber in Manchester"
- "best coffee shop near me"
- "emergency dentist Liverpool"

## Building Local Citations

List your business on:
- Yelp
- Yellow Pages
- Industry directories
- Local business associations

## Getting Reviews

Reviews are crucial for local SEO:
- Ask satisfied customers
- Make it easy with a direct link
- Respond to every review`,
    category: "seo",
    categoryLabel: "SEO",
    date: "Dec 5, 2025",
    readTime: "11 min read",
    image: "https://images.unsplash.com/photo-1553729459-efe14ef6055d?w=800&q=80",
    featured: false,
  },
  {
    id: 8,
    slug: "viral-tiktok-hooks",
    title: "Creating Viral TikTok Hooks: First 3 Seconds Matter",
    excerpt: "The first 3 seconds of your TikTok determine everything. Learn how to create hooks that stop the scroll and keep viewers watching.",
    content: `On TikTok, you have about 1-3 seconds to capture attention. Here's how to make them count.

## Why Hooks Matter

The algorithm measures watch time. If people scroll past in the first second, your video is dead.

## Types of Effective Hooks

### The Question Hook
"Want to know how I made £10k in one month?"

### The Shock Hook
"I can't believe this actually worked..."

### The Tutorial Hook
"Here's how to get 10x more views on TikTok"

### The Story Hook
"So this crazy thing happened to me yesterday..."

## Hook Formulas That Work

1. Start mid-action
2. Use pattern interrupts
3. Promise value immediately
4. Create curiosity gaps
5. Use trending sounds strategically

## Testing Your Hooks

Create 3-5 versions of the same video with different hooks. See which performs best and learn from the data.`,
    category: "tiktok",
    categoryLabel: "TikTok",
    date: "Dec 3, 2025",
    readTime: "6 min read",
    image: "https://images.unsplash.com/photo-1585829365295-ab7cd400c167?w=800&q=80",
    featured: false,
  },
];

export default function Blog() {
  const [activeCategory, setActiveCategory] = useState("all");

  const filteredPosts = activeCategory === "all" 
    ? blogPosts 
    : blogPosts.filter(post => post.category === activeCategory);

  const featuredPosts = blogPosts.filter(post => post.featured);

  return (
    <div data-testid="blog-page" className="pt-24 md:pt-32 bg-black min-h-screen">
      <Helmet>
        <title>Digital Marketing Blog | Tips & Insights for Small Businesses | Barton</title>
        <meta name="description" content="Expert advice on web design, social media, SEO, and digital marketing. Free tips and insights to help your small business grow online." />
        <meta property="og:title" content="Digital Marketing Blog | Barton" />
        <meta property="og:description" content="Expert advice on web design, social media, SEO, and digital marketing. Free tips to help your small business grow online." />
        <meta property="og:url" content="https://barton.uk/blog" />
        <meta property="og:image" content="https://barton.uk/og-image.png" />
        <meta name="twitter:title" content="Digital Marketing Blog | Barton" />
        <meta name="twitter:description" content="Expert advice on web design, social media, SEO, and digital marketing for small businesses." />
        <meta name="twitter:image" content="https://barton.uk/og-image.png" />
      </Helmet>
      {/* Hero Section */}
      <section className="container-custom py-12 md:py-16">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <span className="inline-block px-4 py-2 rounded-full bg-gradient-to-r from-purple-500 to-pink-500/10 text-pink-400 text-sm font-medium mb-6">
            Blog
          </span>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6 text-white">
            Insights & <span className="text-gradient">Resources</span>
          </h1>
          <p className="text-white/60 text-lg leading-relaxed">
            Tips, guides, and strategies to help your small business grow online. 
            From TikTok to SEO, we cover it all.
          </p>
        </div>

        {/* Featured Posts */}
        <div className="grid md:grid-cols-2 gap-6 mb-16">
          {featuredPosts.map((post, index) => (
            <Link 
              to={`/blog/${post.slug}`}
              key={post.id}
              data-testid={`featured-post-${post.id}`}
              className={`group relative overflow-hidden rounded-2xl border border-white/10 ${
                index === 0 ? "hover:border-pink-500/50" : "hover:border-purple-500/50"
              } transition-all duration-300 block`}
            >
              <div className="relative h-64 md:h-80">
                <img 
                  src={post.image} 
                  alt={post.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium mb-3 ${
                  index === 0 ? "bg-gradient-to-r from-purple-500 to-pink-500/20 text-pink-400" : "bg-gradient-to-r from-blue-500 to-purple-500/20 text-purple-400"
                }`}>
                  {post.categoryLabel}
                </span>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-white mb-2 group-hover:text-pink-400 transition-colors">
                  {post.title}
                </h2>
                <p className="text-white/60 text-sm mb-4 line-clamp-2">{post.excerpt}</p>
                <div className="flex items-center gap-4 text-white/40 text-xs">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> {post.date}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {post.readTime}
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Categories */}
        <div className="flex flex-wrap gap-2 justify-center mb-12">
          {categories.map((category) => (
            <button
              key={category.slug}
              data-testid={`category-${category.slug}`}
              onClick={() => setActiveCategory(category.slug)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                activeCategory === category.slug
                  ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white"
                  : "bg-white/5 text-white/60 hover:bg-white/10 hover:text-white"
              }`}
            >
              {category.name}
            </button>
          ))}
        </div>

        {/* Blog Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPosts.map((post, index) => (
            <Link 
              to={`/blog/${post.slug}`}
              key={post.id}
              data-testid={`blog-post-${post.id}`}
              className={`group bg-[#111111] rounded-2xl overflow-hidden border border-white/10 transition-all duration-300 hover:-translate-y-2 block ${
                index % 2 === 0 ? "hover:border-pink-500/50 hover:shadow-[0_0_30px_rgba(248,24,148,0.1)]" : "hover:border-purple-500/50 hover:shadow-[0_0_30px_rgba(30,144,255,0.1)]"
              }`}
            >
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={post.image} 
                  alt={post.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#111111] to-transparent" />
              </div>
              <div className="p-6">
                <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium mb-3 ${
                  index % 2 === 0 ? "bg-gradient-to-r from-purple-500 to-pink-500/10 text-pink-400" : "bg-gradient-to-r from-blue-500 to-purple-500/10 text-purple-400"
                }`}>
                  {post.categoryLabel}
                </span>
                <h3 className="font-heading text-lg font-bold text-white mb-2 group-hover:text-pink-400 transition-colors line-clamp-2">
                  {post.title}
                </h3>
                <p className="text-white/60 text-sm mb-4 line-clamp-2">{post.excerpt}</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 text-white/40 text-xs">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" /> {post.date}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {post.readTime}
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-black">
        <div className="container-custom">
          <div className="relative rounded-3xl p-8 md:p-16 text-center text-white overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 via-transparent to-pink-500/20" />
            <div className="absolute inset-0 border border-white/10 rounded-3xl" />
            
            <div className="relative z-10">
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                Want us to help grow your business?
              </h2>
              <p className="text-white/60 text-lg mb-8 max-w-2xl mx-auto">
                Let's put these strategies into action for your brand. Get in touch for a free consultation.
              </p>
              <Button
                asChild
                data-testid="blog-cta-button"
                className="bg-gradient-to-r from-purple-500 to-pink-500 hover:bg-gradient-to-r from-purple-500 to-pink-500/90 text-white rounded-full px-10 py-6 text-lg"
              >
                <Link to="/contact">
                  Get in Touch <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
